import { specifications, type BodyType } from "@tailor-frame/patterns";

export type CatalogueEntry = (typeof specifications)[number];
export type Audience = "men" | "women" | "children";

/**
 * The books (modules) the library is drafted from, in the order they show.
 * Each book is a row of the select screen; a book with no patterns yet still shows as an empty slot.
 */
export interface Collection { id: string; name: string; source: string; audience?: Audience; matches: (patternId: string) => boolean }

export const collections: Collection[] = [
  { id: "classic-menswear", name: "Classic menswear", source: "Aldrich, Metric Pattern Cutting for Menswear", audience: "men", matches: () => true },
  { id: "classic-womenswear", name: "Classic womenswear", source: "Aldrich, Metric Pattern Cutting for Women's Wear", audience: "women", matches: (id) => id.startsWith("womens-") },
  { id: "classic-childrenswear", name: "Classic childrenswear", source: "Aldrich, Metric Pattern Cutting for Children's Wear", audience: "children", matches: (id) => id.startsWith("child-") || id.startsWith("baby-") },
  { id: "supreme-system", name: "Supreme System menswear", source: "Croonborg, New Supreme System, 1917", audience: "men", matches: (id) => id.startsWith("supreme-") },
  { id: "fixtures", name: "Test fixtures", source: "Engine test patterns", matches: () => false },
];

export const audiences: { id: Audience; label: string }[] = [
  { id: "men", label: "Menswear" },
  { id: "women", label: "Womenswear" },
  { id: "children", label: "Childrenswear" },
];

/** The patterns drafted for a body type, which the library shows first. */
export const audienceForBody = (type: BodyType): Audience => type === "masculine" ? "men" : type === "feminine" ? "women" : "children";

/**
 * Garment types, in the order their subsections show. Anything else sorts after them.
 * A jacket is worn indoors and can go under a coat; a coat (an overgarment) is for outdoors.
 */
const typeOrder = ["Bodices", "Dresses", "Shirts", "Jersey tops", "Waistcoats", "Suit jackets", "Jackets", "Coats", "Overcoats", "Capes", "Overalls", "Trousers", "Skirts", "Swimwear", "Sleepwear", "Other", "Test fixtures"];

export const garmentType = (entry: CatalogueEntry) => entry.specification.pattern.category ?? "Other";

export function collectionFor(entry: CatalogueEntry): Collection {
  const { id, category } = entry.specification.pattern;
  if (category === "Test fixtures") return collections.find((collection) => collection.id === "fixtures")!;
  // The catch-all classic menswear book is tried last, so a more specific book wins.
  return [...collections.slice(1), collections[0]].find((collection) => collection.matches(id))!;
}

export interface Subsection { title: string; entries: CatalogueEntry[] }
export interface Section { id: string; title: string; detail?: string; subsections: Subsection[] }

function byType(entries: CatalogueEntry[]): Subsection[] {
  const groups = new Map<string, CatalogueEntry[]>();
  for (const entry of entries) groups.set(garmentType(entry), [...(groups.get(garmentType(entry)) ?? []), entry]);
  const rank = (type: string) => (typeOrder.indexOf(type) + 1 || typeOrder.length + 1) - 1;
  return [...groups].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b)).map(([type, items]) => ({ title: type, entries: items }));
}

/**
 * The select screen's rows: one per book with its garment types as subsections,
 * or, grouped by type, one per garment type with every book's patterns together.
 */
export function catalogueSections(grouping: "book" | "type", audience: Audience | "all", showFixtures: boolean): Section[] {
  const visible = specifications.filter((entry) => {
    const collection = collectionFor(entry);
    if (collection.id === "fixtures") return showFixtures;
    return audience === "all" || collection.audience === audience;
  });
  if (grouping === "type") {
    return byType(visible).map((subsection) => ({ id: `type-${subsection.title}`, title: subsection.title, subsections: [{ title: "", entries: subsection.entries }] }));
  }
  return collections
    .filter((collection) => collection.id === "fixtures" ? showFixtures : audience === "all" || collection.audience === audience)
    .map((collection) => ({ id: collection.id, title: collection.name, detail: collection.source, subsections: byType(visible.filter((entry) => collectionFor(entry) === collection)) }));
}
